import { type MutationOptions, useMutation } from '@tanstack/react-query';
import { App } from 'antd';
import { useShallow } from 'zustand/shallow';

import { api } from '@/api';
import { END_POINT } from '@/constants/end-point.constants';
import { useCommonStore } from '@/store/common.store';

import type {
  ShopRejectionRequest,
  ShopRequest,
  ShopWeddingRequest,
} from '../types';

function parsedRequestBody(requestBody: ShopRequest | ShopWeddingRequest) {
  const {
    operation_end_date: operationEndDate,
    operation_start_date: operationStartDate,
    ...rest
  } = requestBody;
  return {
    ...rest,
    operation_start_date: operationStartDate?.startOf('day').toISOString(),
    operation_end_date: operationEndDate?.endOf('day').toISOString(),
  };
}

const deleteShop = (id: number) =>
  api.delete(END_POINT.shops, { data: { ids: [id] } });

export const useDeleteShop = (
  mutationOptions?: MutationOptions<unknown, unknown, unknown>,
) => {
  const { message } = App.useApp();
  const setIsProcessing = useCommonStore(state => state.setIsProcessing);

  return useMutation({
    mutationFn: (id: number) => deleteShop(id),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess(...arguments_) {
      mutationOptions?.onSuccess?.(...arguments_);
      message.success('削除しました');
    },
  });
};

const updateShop = (
  isTemporariesShop: boolean,
  id: string,
  requestBody: ShopRequest,
) =>
  api.put(
    isTemporariesShop
      ? END_POINT.shopTemporariesUpdate(id)
      : END_POINT.shopDetail(id),
    parsedRequestBody(requestBody),
  );

export const useUpdateShop = (
  isTemporariesShop = false,
  mutationOptions?: MutationOptions<unknown, unknown, unknown>,
) => {
  const { message } = App.useApp();
  const { setIsFormChanged, setIsProcessing, setIsFormUpdated } =
    useCommonStore(
      useShallow(({ setIsFormChanged, setIsProcessing, setIsFormUpdated }) => ({
        setIsFormChanged,
        setIsProcessing,
        setIsFormUpdated,
      })),
    );

  return useMutation({
    mutationFn: ({
      id,
      requestBody,
    }: {
      id: string;
      requestBody: ShopRequest;
    }) => updateShop(isTemporariesShop, id, requestBody),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess: async (...arguments_) => {
      setIsFormChanged(false);
      setIsFormUpdated(true);
      await mutationOptions?.onSuccess?.(...arguments_);
      message.success('更新しました');
    },
  });
};

const updateWeddingShop = (id: string, requestBody: ShopWeddingRequest) =>
  api.put(END_POINT.shopWeddingDetail(id), parsedRequestBody(requestBody));

export const useUpdateWeddingShop = (
  mutationOptions?: MutationOptions<unknown, unknown, unknown>,
) => {
  const { message } = App.useApp();
  const { setIsFormChanged, setIsProcessing, setIsFormUpdated } =
    useCommonStore(
      useShallow(({ setIsFormChanged, setIsProcessing, setIsFormUpdated }) => ({
        setIsFormChanged,
        setIsProcessing,
        setIsFormUpdated,
      })),
    );

  return useMutation({
    mutationFn: ({
      id,
      requestBody,
    }: {
      id: string;
      requestBody: ShopWeddingRequest;
    }) => updateWeddingShop(id, requestBody),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess: async (...arguments_) => {
      setIsFormChanged(false);
      setIsFormUpdated(true);
      await mutationOptions?.onSuccess?.(...arguments_);
      message.success('更新しました');
    },
  });
};

const rejectShop = (id: string, requestBody: ShopRejectionRequest) =>
  api.post(END_POINT.shopRejectRequests(id), requestBody);

export const useRejectShop = ({
  onSuccess,
}: MutationOptions<unknown, unknown, unknown>) => {
  const { message } = App.useApp();
  const setIsProcessing = useCommonStore(state => state.setIsProcessing);

  return useMutation({
    mutationFn: ({
      id,
      requestBody,
    }: {
      id: string;
      requestBody: ShopRejectionRequest;
    }) => rejectShop(id, requestBody),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess(...arguments_) {
      onSuccess?.(...arguments_);
      message.success('申請を差戻しました');
    },
  });
};

const approveShop = (id: string) => api.post(END_POINT.shopApproveRequests(id));

export const useApproveShop = ({
  onSuccess,
}: MutationOptions<unknown, unknown, unknown>) => {
  const { message } = App.useApp();
  const setIsProcessing = useCommonStore(state => state.setIsProcessing);

  return useMutation({
    mutationFn: (id: string) => approveShop(id),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess(...arguments_) {
      onSuccess?.(...arguments_);
      message.success('申請を承認しました');
    },
  });
};

const withdrawShop = (id: string) =>
  api.post(END_POINT.shopWithdrawRequests(id));

export const useWithdrawShop = ({
  onSuccess,
}: MutationOptions<unknown, unknown, unknown>) => {
  const { message } = App.useApp();
  const setIsProcessing = useCommonStore(state => state.setIsProcessing);

  return useMutation({
    mutationFn: (id: string) => withdrawShop(id),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess(...arguments_) {
      onSuccess?.(...arguments_);
      message.success('申請を取下げました');
    },
  });
};
